import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { API } from "@/lib/api";
import { ReportBody } from "@/components/ReportBody";
import { Building2, Loader2, Camera } from "lucide-react";
import AssignFlowLogo from "@/components/AssignFlowLogo";

function getSessionId() {
  const KEY = "af_pub_session";
  let v = localStorage.getItem(KEY);
  if (!v) {
    v = crypto.randomUUID ? crypto.randomUUID() : `s-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    localStorage.setItem(KEY, v);
  }
  return v;
}

export default function PublicSnapshot() {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const buyerId = searchParams.get("b") || null;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const sessionId = useRef(getSessionId());

  useEffect(() => {
    axios.get(`${API}/public/snapshot/${token}`)
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [token]);

  const onPhotoView = useCallback((idx) => {
    const total = (data?.lead?.photos || []).length || 1;
    axios.post(`${API}/public/snapshot/${token}/photo-view`, {
      photo_index: idx,
      total_photos: total,
      session_id: sessionId.current,
      source: "snapshot",
      team_member_id: buyerId,
    }).catch(() => { /* best-effort */ });
  }, [data, token, buyerId]);

  return (
    <div className="min-h-screen bg-[var(--bg)]">
      <header className="bg-[var(--ink)] text-white print:hidden">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <span className="w-7 h-7 rounded-md bg-[var(--primary)] flex items-center justify-center"><AssignFlowLogo size={16} accent="#fff" /></span>
            <span className="font-display font-extrabold">iSpeedToClose</span>
          </Link>
          <span className="text-xs text-white/55 uppercase tracking-widest flex items-center gap-1.5"><Camera size={11} /> Frozen snapshot</span>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-8">
        {loading ? (
          <div className="card text-center py-16 text-[var(--ink-2)]">
            <Loader2 size={20} className="animate-spin mx-auto mb-3" /> Loading…
          </div>
        ) : !data ? (
          <div className="card text-center py-16">
            <div className="font-display font-bold text-xl mb-2">Snapshot not found</div>
            <p className="text-[var(--ink-2)] mb-5">This link may have been deleted by the sender or doesn't exist.</p>
            <Link to="/" className="btn-outline inline-flex">Visit iSpeedToClose</Link>
          </div>
        ) : (
          <>
            {(data.snapshot?.name || data.snapshot?.created_at) && (
              <div className="card-flat px-4 py-3 mb-4 flex items-center justify-between gap-3 text-xs text-[var(--ink-2)]" data-testid="snapshot-banner">
                <div className="flex items-center gap-2">
                  <Camera size={13} className="text-[var(--primary)]" />
                  <span>
                    <span className="font-semibold text-[var(--ink)]">Snapshot{data.snapshot?.name ? `: "${data.snapshot.name}"` : ""}</span>
                    {" · frozen on "}
                    <span className="font-mono">{new Date(data.snapshot.created_at).toLocaleString()}</span>
                  </span>
                </div>
                <span className="italic">Values shown reflect the report at the time it was saved.</span>
              </div>
            )}
            <ReportBody lead={data.lead} report={data.report} onPhotoView={onPhotoView} />
          </>
        )}
      </main>
    </div>
  );
}
